import { Component, Input, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core';
import { FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { Subscription, lastValueFrom } from 'rxjs';
import { DataSavedMessage } from 'src/app/shared/models/mediator-messages.model';
import { MediatorService } from 'src/app/shared/services/mediator.service';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { FormationCementCatalog } from '../models/formations-cement-catalog.model';
import { CatalogScope, CatalogFilterMode } from '../models/catalog-scope.enum';
import { FormationsService } from '../shared/services/formations-cement-catalog.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { saveAs } from 'file-saver-es';
import ExcelJS from "exceljs";
import { Utilities } from 'src/app/core/services/utilities';
import { GetSolidsDensityFromLbmFt, GetValueFromPsi } from 'src/app/perivis/shared/helpers/units.helper';
import { StoreService, StorageKeys } from 'src/app/core/services/store.service';
import { AppNotificationService } from 'src/app/shared/services/app-notification.service';
import { UserRoles } from 'src/app/core/components/user-admin-page/user-model';
import { cannotDeleteCatalogItem } from '../shared/catalog-helpers';
import { getDuplicateCatalogColors } from '../shared/helpers/catalog-duplicate-highlight.helper';

@Component({
  selector: 'app-formations-cement-catalog',
  templateUrl: './formations-cement-catalog.component.html',
  styles: `
    .catalog-row-disabled > td:not(:first-child) {
      opacity: 0.6;
      pointer-events: none;
    }
  `,
  providers: [DialogService, ConfirmationService],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormationsCementCatalogComponent implements OnInit, OnDestroy {
  public control(group: FormGroup, name: string): UntypedFormControl {
    return group.get(name) as UntypedFormControl;
  }

  private _items: FormationCementCatalog[];
  private _subscriptions: Subscription = new Subscription();

  @Input() catalogType = '';
  @ViewChild("catalogTable") catalogTable;

  public columnDefs: any[];
  public isLoading = signal<boolean>(true);
  public catalogForm: UntypedFormGroup;
  public youngsModulusUnit: string;
  public thermalCondUnit: string;
  public solidsDensityUnit: string;
  public thermalExpCoeffUnit: string;
  public specificHeatCapacityUnit: string;
  public validationRules = {
    density: { min: 0.001, max: 1000 },
    specificHeatCapacity: { min: 0.001, max: 5 },
    thermalConductivity: { min: 0.001, max: 2000 },
    youngsModulus: { min: 0.001, max: 250000000 },
    poissonsRatio: { min: 0, max: 0.5 },
    thermalExpansionCoefficient: { min: 0, max: 100 }
  };
  public tableHeight: string = '600px';

  // Scope filtering
  public CatalogScope = CatalogScope;
  public selectedScopeFilter: CatalogFilterMode = CatalogFilterMode.AllItems;
  public userRoles: UserRoles; // Add userRoles for scope filter component
  public duplicateRowColors = signal<ReadonlyMap<string, string>>(new Map());

  get itemsCatalog(): UntypedFormArray {
    return this.catalogForm.get("items") as UntypedFormArray;
  }

  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  // State
  @Input()
  public componentId: string;

  constructor(
    private _messenger: MediatorService,
    private _confirmationService: ConfirmationService,
    private _catalogService: FormationsService,
    private _formBuilder: UntypedFormBuilder,
    private _store: StoreService,
    private _toaster: AppNotificationService
  ) {
    this.catalogForm = this._formBuilder.group({
      items: this._formBuilder.array([]),
    });

    this._subscriptions.add(
      this.itemsCatalog.valueChanges.subscribe(() => this.refreshDuplicateRowColors())
    );
  }

  async ngOnInit(): Promise<void> {
    this.userRoles = await this._store.get(StorageKeys.ROLES);
    const uu = await this._store.get<UserUnitsModel>(StorageKeys.UNITS);
    this.youngsModulusUnit = uu.stressPressure;
    this.thermalCondUnit = uu.thermalConductivity;
    this.solidsDensityUnit = uu.solidsDensity;
    this.thermalExpCoeffUnit = unitsLib[uu.coefficientOfThermalExpansion].symbol;
    this.specificHeatCapacityUnit = unitsLib[uu.specificHeatCapacity].symbol;
    this.validationRules = this.getValidationRules();

    this.columnDefs = [
      { field: 'name', header: 'Name' },
      { field: 'density', header: `Density (${this.solidsDensityUnit})` },
      { field: 'specificHeatCapacity', header: `Specific Heat Capacity (${this.specificHeatCapacityUnit})` },
      { field: 'thermalConductivity', header: `Thermal Conductivity (${this.thermalCondUnit})` },
      { field: 'youngsModulus', header: `Young's Modulus (${this.youngsModulusUnit})` },
      { field: 'poissonsRatio', header: 'Poisson\'s Ratio' },
      { field: 'thermalExpansionCoefficient', header: `Thermal Exp. Coeff (${this.thermalExpCoeffUnit})` }
    ];

    this._catalogService.getFormationCement(this.selectedScopeFilter).subscribe({
      next: (items) => {
        this._items = items.filter(x => x.discriminator == this.catalogType);

        const defaultItem = this._items.find(x => x.name == 'Default');
        if (defaultItem) {
          this._items.splice(this._items.indexOf(defaultItem), 1);
          this._items.unshift(defaultItem);
        }

        this.populateFormData(this._items);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });

    addEventListener("keydown", (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
    });
  }

  public onScopeFilterChange(): void {
    this.itemsCatalog.clear();
    this.isLoading.set(true);
    this._catalogService.getFormationCement(this.selectedScopeFilter).subscribe({
      next: (items) => {
        this._items = items.filter(x => x.discriminator == this.catalogType);

        const defaultItem = this._items.find(x => x.name == 'Default');
        if (defaultItem) {
          this._items.splice(this._items.indexOf(defaultItem), 1);
          this._items.unshift(defaultItem);
        }

        this.populateFormData(this._items);
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
      }
    });
  }

  public onAddItem(idx?: number): void {
    if (this.itemsCatalog.invalid) {
      return;
    }
    this.isLoading.set(true);
    this.itemsCatalog.insert(idx || 1, this.newItem());
    this.catalogTable?.scrollTo({ top: 1, behavior: 'smooth' });
    setTimeout(() => {
      this.isLoading.set(false);
    }, 500);
  }

  public onDeleteItem(idx: number): void {
    const itemId = this.itemsCatalog.controls[idx].get("id").value;
    const itemName = this.itemsCatalog.controls[idx].get("name").value;

    if (itemId.length < 36) {
      this.itemsCatalog.removeAt(idx);
    } else {
      const catalogType = this.catalogType == 'formation' ? ' formation?' : ' hard cement?'
      this._confirmationService.confirm({
        message: `Are you sure that you want to delete the selected "${itemName}"` + catalogType,
        accept: async () => {
          if (itemId.length > 24) { // it's been saved to the db, so delete through API
            await lastValueFrom(this._catalogService.deleteItem(itemId));
            this._messenger.publish(new DataSavedMessage(this.catalogType == 'formation' ? 'Formation Catalog' : 'Hard Cement Catalog'));
          }
          this.itemsCatalog.removeAt(idx);
        }
      });
    }
  }

  public deleteItemCheck(itemFg: FormGroup): boolean {
    return cannotDeleteCatalogItem(itemFg, this.userRoles);
  }

  public showJson(data: any) {
    return JSON.stringify(data, null, '\t');
  }

  public setHoverTitle(event: Event, title: string): void {
    const target = event.currentTarget as HTMLElement | null;
    if (target) {
      target.title = title;
    }
  }

  public getScopeLabel(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'Global' : 'User';
  }

  public getScopeBadgeClass(scope: CatalogScope): string {
    return scope === CatalogScope.Global ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800';
  }

  public async toggleScope(formGroup: UntypedFormGroup): Promise<void> {
    this.isLoading.set(true);
    const currentScope = formGroup.get('scope').value;
    const newScope = currentScope === CatalogScope.User ? CatalogScope.Global : CatalogScope.User;

    const itemData = formGroup.value;
    itemData.scope = newScope;

    await lastValueFrom(this._catalogService.updateItem(new FormationCementCatalog(itemData)));

    // Only update the form if save succeeds
    formGroup.get('scope').patchValue(newScope, { emitEvent: false });

    // Update the local cache
    const index = this._items.findIndex(x => x.id === itemData.id);
    if (index > -1) {
      this._items[index].scope = newScope;
    }

    this._messenger.publish(new DataSavedMessage(this.catalogType == 'formation' ? 'Formations Catalog' : 'Hard Cement Catalog'));
    this.isLoading.set(false);
  }

  private async handleSaveCatalogRow(v: any, catalogRecord: UntypedFormGroup): Promise<void> {
    const name = v.name;
    let existingItem = this._items.find(x =>
      x.name == name
      && this.approximatelyEqual(x.density, v.density)
      && this.approximatelyEqual(x.specificHeatCapacity, v.specificHeatCapacity)
      && this.approximatelyEqual(x.thermalConductivity, v.thermalConductivity)
      && this.approximatelyEqual(x.youngsModulus, v.youngsModulus)
      && this.approximatelyEqual(x.poissonsRatio, v.poissonsRatio)
      && this.approximatelyEqual(x.thermalExpansionCoefficient, v.thermalExpansionCoefficient)
      && x.scope == v.scope
    );
    if (existingItem) {
      return;
    }

    if (catalogRecord.valid && !this.isLoading()) {
      existingItem = this._items.find(x => x.name == name && x.id != v.id);
      if (existingItem) {
        this._toaster.showError(`Item with name "${name}" already exists.`);
        return;
      }
      if (v.id.length == 24) { // it's only on the client so save to db as a new record
        this.isLoading.set(true);
        const newEntityId = await lastValueFrom(this._catalogService.addItem(new FormationCementCatalog(v)));
        catalogRecord.get("id").patchValue(newEntityId, { emitEvent: false });
        this._items.unshift(catalogRecord.value);
        this.isLoading.set(false);
      } else {
        await lastValueFrom(this._catalogService.updateItem(new FormationCementCatalog(v)));
        const itemIndex = this._items.findIndex(x => x.id == v.id);
        if (itemIndex > -1) {
          this._items[itemIndex] = catalogRecord.value;
        }
      }
      this._messenger.publish(new DataSavedMessage(this.catalogType == 'formation' ? 'Formations Catalog' : 'Hard Cement Catalog'));
    }
  }

  approximatelyEqual(a, b) {
    if (a === b) return true;
    if (typeof a !== 'number' || typeof b !== 'number') return false;
    return Math.abs(a - b) < Number.EPSILON * Math.max(Math.abs(a), Math.abs(b));
  }

  private newItem(): UntypedFormGroup {
    const specHeatCapacityDefault = this.catalogType == 'formation' ? this.specificHeatCapacityUnit == 'BTU/lbm-°F' ? 0.3 : 1256 : this.specificHeatCapacityUnit == 'BTU/lbm-°F' ? 0.2 : 837.9;
    const densityDefault = this.catalogType == 'formation' ? GetSolidsDensityFromLbmFt(140, this.solidsDensityUnit) : GetSolidsDensityFromLbmFt(160, this.solidsDensityUnit);
    const thermalConductivityDefault = this.catalogType == 'formation' ? this.thermalCondUnit == 'BTU/h·ft·°F' ? 0.92 : 1.592 : this.thermalCondUnit == 'BTU/h·ft·°F' ? 0.568 : 0.98;
    const thermalExpansionCoefficientDefault = this.catalogType == 'formation' ? this.thermalExpCoeffUnit == '1E-6/°F' ? 3 : 5.4 : this.thermalExpCoeffUnit == '1E-6/°F' ? 6 : 10.8;

    const itemFg = new UntypedFormGroup({
      // Setting a unique id here so that prime-ng p-table has a data key for the expand and collapse functionality.
      id: new UntypedFormControl(Utilities.generateFakeGuid()),
      name: new UntypedFormControl("", { updateOn: 'blur', validators: [Validators.required] }),
      density: new UntypedFormControl(densityDefault, { updateOn: 'blur', validators: [Validators.required] }),
      specificHeatCapacity: new UntypedFormControl(specHeatCapacityDefault, { updateOn: 'blur', validators: [Validators.required] }),
      thermalConductivity: new UntypedFormControl(thermalConductivityDefault, { updateOn: 'blur', validators: [Validators.required] }),
      youngsModulus: new UntypedFormControl(3000000, { updateOn: 'blur', validators: [Validators.required] }),
      poissonsRatio: new UntypedFormControl(this.catalogType == 'formation' ? 0.18 : 0.3, { updateOn: 'blur', validators: [Validators.min(0), Validators.max(0.5), Validators.required] }),
      thermalExpansionCoefficient: new UntypedFormControl(thermalExpansionCoefficientDefault, { updateOn: 'blur', validators: [Validators.required] }),
      discriminator: new UntypedFormControl(this.catalogType),
      isLocked: new UntypedFormControl(false),
      metadata: new UntypedFormControl(null),
      scope: new UntypedFormControl(CatalogScope.User)
    });
    
    this._subscriptions.add(itemFg.valueChanges.subscribe((v) => this.handleSaveCatalogRow(v, itemFg)));

    itemFg.markAllAsTouched();

    return itemFg;
  }

  private getValidationRules() {
    const stress = (value: number): number => GetValueFromPsi(value, this.youngsModulusUnit);
    const density = (value: number): number => GetSolidsDensityFromLbmFt(value, this.solidsDensityUnit);
    const thermalExpansion = (value: number): number => this.thermalExpCoeffUnit === '1E-6/°C' ? value * 1.8 : value;
    const specificHeat = (value: number): number => this.specificHeatCapacityUnit === 'J/kg-K' ? value * 4186.8 : value;
    const thermalConductivity = (value: number): number => this.thermalCondUnit === 'W/m·K' ? value * 1.730735 : value;

    return {
      density: { min: density(0.001), max: density(1000) },
      specificHeatCapacity: { min: specificHeat(0.001), max: specificHeat(5) },
      thermalConductivity: { min: thermalConductivity(0.001), max: thermalConductivity(2000) },
      youngsModulus: { min: stress(0.001), max: stress(250000000) },
      poissonsRatio: { min: 0, max: 0.5 },
      thermalExpansionCoefficient: { min: thermalExpansion(0), max: thermalExpansion(100) }
    };
  }

  private populateFormData(items: FormationCementCatalog[] = null): void {
    items.forEach((data, idx) => {
      const catalogFg = this.newItem();
      this.itemsCatalog.push(catalogFg);
      const expandedData = { ...data };
      this.itemsCatalog.controls[idx].setValue(expandedData, { emitEvent: false });
      this.syncGradeRowState(this.itemsCatalog.controls[idx] as FormGroup);
    });
    this.refreshDuplicateRowColors();
  }

  private refreshDuplicateRowColors(): void {
    if (!this.userRoles?.catalogAdmin) {
      this.duplicateRowColors.set(new Map());
      return;
    }

    this.duplicateRowColors.set(getDuplicateCatalogColors(this.itemsCatalog.getRawValue(), item => ({
      name: item.name ?? null,
      density: item.density ?? null,
      specificHeatCapacity: item.specificHeatCapacity ?? null,
      thermalConductivity: item.thermalConductivity ?? null,
      youngsModulus: item.youngsModulus ?? null,
      poissonsRatio: item.poissonsRatio ?? null,
      thermalExpansionCoefficient: item.thermalExpansionCoefficient ?? null,
      discriminator: item.discriminator ?? null,
      scope: item.scope ?? null
    })));
  }

  private syncGradeRowState(itemFg: FormGroup): void {
    if (this.updateFormDisabledState(itemFg)) {
      itemFg.disable({ emitEvent: false });
      return;
    }

    itemFg.enable({ emitEvent: false });
  }

  public updateFormDisabledState(formGroup: FormGroup): boolean {
    return this.deleteItemCheck(formGroup) || this.userRoles?.readOnly;
  }

  public async exportExcel(): Promise<void> {

    const mappedData = this.itemsCatalog.controls.map(td => {
      const objWithHeaderKeys = {};
      this.columnDefs.forEach(cd => {
        const objHeaderKey = cd.header;
        objWithHeaderKeys[objHeaderKey.replace(/["']/g, '"')] = td['controls'][cd.field].value;
      });
      return objWithHeaderKeys;
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Connections Catalog");

    worksheet.columns = Object.keys(mappedData[0]).map((key) => ({
      header: key,
      key: key,
      width: 45
    }));

    worksheet.addRows(mappedData);

    const buffer = await workbook.xlsx.writeBuffer();

    const catalogType = this.catalogType == 'formation' ? 'Formations Catalog?' : 'Hard Cement Catalog?'
    this.saveAsExcelFile(buffer, catalogType);
  }

  public saveAsExcelFile(buffer: any, fileName: string): void {
    const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
    const EXCEL_EXTENSION = '.xlsx';
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE
    });
    saveAs(data, fileName + '_export_' + EXCEL_EXTENSION);
  }

  public filterGrades(event: any) {
    const filterName = event['srcElement']['value'];
    let filtered: FormationCementCatalog[] = this._items;
    if (filterName) {
      filtered = this._items.filter((grade) => {
        return grade.name.toLowerCase().startsWith(filterName.toLowerCase());
      });
    }
    this.itemsCatalog.clear();
    this.populateFormData(filtered);
    this.catalogTable?.scrollTo({ top: 0, behavior: 'smooth' });
  }

  ngOnDestroy() {
    this._subscriptions?.unsubscribe();
  }
}
